/**
 * Home に置けるウィジェットの一覧。
 *
 * 既定の大きさと出せるかどうかをここで決める（1x1〜4x4 から選べる。fixedSize のものは変更不可）。
 * 実際の見た目は home-widgets 側のコンポーネントが持つ。
 */

import { type ReactNode, useMemo } from 'react'

import { HistoryWidget } from '@/components/home/history-widget'
import type { NavigateFn } from '@/components/home/home-widget-data'
import {
  MusicWidget,
  NotificationWidget,
  NutritionWidget,
  StepsWidget,
  TodoWidget,
  WeightWidget,
  WorkoutWidget,
} from '@/components/home/home-widgets'
import { ShortcutWidget } from '@/components/home/shortcut-widget'
import { SleepWidget } from '@/components/home/sleep-widget'
import type { HomeWidgetsData } from '@/components/home/use-home-widgets'
import { WidgetSkeleton } from '@/components/home/widget-card'
import type { WidgetSize } from '@/store/useHomeLayoutStore'
import { useMusicPlayerStore } from '@/store/useMusicPlayerStore'
import { useShortcutStore } from '@/store/useShortcutStore'

export type HomeWidgetDef = {
  key: string
  label: string
  /** 追加したときの大きさ */
  defaultSize: WidgetSize
  /** 大きくしても出せる情報が増えないものは defaultSize に固定する */
  fixedSize?: boolean
  render: (data: HomeWidgetsData, size: WidgetSize, onNavigate: NavigateFn) => ReactNode
}

export const HOME_WIDGETS: HomeWidgetDef[] = [
  {
    key: 'widget:todo',
    label: 'TODO',
    defaultSize: '2x4',
    render: (data, size, onNavigate) =>
      data.isLoading ? (
        <WidgetSkeleton />
      ) : (
        <TodoWidget
          todos={data.todos ?? []}
          size={size}
          onNavigate={onNavigate}
          onAdded={() => void data.reloadTodos()}
        />
      ),
  },
  {
    key: 'widget:steps',
    label: '歩数',
    defaultSize: '1x1',
    fixedSize: true,
    render: (data, _size, onNavigate) =>
      data.isLoading ? <WidgetSkeleton /> : <StepsWidget garmin={data.garmin} onNavigate={onNavigate} />,
  },
  {
    key: 'widget:weight',
    label: '体重',
    defaultSize: '1x1',
    fixedSize: true,
    render: (data, _size, onNavigate) =>
      data.isLoading ? <WidgetSkeleton /> : <WeightWidget weight={data.weight} onNavigate={onNavigate} />,
  },
  {
    key: 'widget:nutrition',
    label: '食事',
    defaultSize: '1x2',
    render: (data, size, onNavigate) =>
      data.isLoading ? (
        <WidgetSkeleton />
      ) : (
        <NutritionWidget nutrition={data.nutrition} size={size} onNavigate={onNavigate} />
      ),
  },
  {
    key: 'widget:workout',
    label: '筋トレ',
    defaultSize: '2x2',
    render: (data, size, onNavigate) =>
      data.isLoading ? (
        <WidgetSkeleton />
      ) : (
        <WorkoutWidget sessions={data.sessions ?? []} size={size} onNavigate={onNavigate} />
      ),
  },
  {
    key: 'widget:music',
    label: 'ミュージック',
    defaultSize: '2x2',
    render: (_data, size, onNavigate) => <MusicWidget size={size} onNavigate={onNavigate} />,
  },
  {
    key: 'widget:shortcut',
    label: 'ショートカット',
    defaultSize: '2x2',
    render: (_data, size, onNavigate) => <ShortcutWidget size={size} onNavigate={onNavigate} />,
  },
  {
    key: 'widget:history',
    label: '履歴',
    defaultSize: '2x2',
    render: (data, size, onNavigate) =>
      data.isLoading ? (
        <WidgetSkeleton />
      ) : (
        <HistoryWidget items={data.recentProducts ?? []} size={size} onNavigate={onNavigate} />
      ),
  },
  {
    key: 'widget:sleep',
    label: '次の睡眠',
    defaultSize: '2x2',
    render: (_data, size, onNavigate) => <SleepWidget size={size} onNavigate={onNavigate} />,
  },
  {
    key: 'widget:notification',
    label: 'ジョブ',
    defaultSize: '2x4',
    render: (data, size, onNavigate) =>
      data.isLoading ? (
        <WidgetSkeleton />
      ) : (
        <NotificationWidget
          running={data.runningTasks ?? []}
          todayErrors={data.todayErrors ?? 0}
          size={size}
          onNavigate={onNavigate}
        />
      ),
  },
]

/**
 * 出せるウィジェットのキー。
 *
 * 取得結果ではなく権限で決める。データの有無で判定すると
 * レスポンスが返る前後で枚数が変わり、アイコンの位置が跳ねるため。
 * ミュージックとショートカットだけは API ではなくストアを見る。
 */
export const useAvailableWidgetKeys = (data: HomeWidgetsData): Set<string> => {
  const hasTrack = useMusicPlayerStore((state) => Boolean(state.queue[state.currentIndex] ?? state.playHistory[0]))
  const hasShortcut = useShortcutStore((state) => state.shortcuts.length > 0)
  const { permissions } = data

  return useMemo(() => {
    const keys = new Set<string>()
    if (permissions.todo) keys.add('widget:todo')
    if (permissions.activity) keys.add('widget:steps')
    if (permissions.nutrition) keys.add('widget:weight')
    if (permissions.nutrition) keys.add('widget:nutrition')
    if (permissions.workout) keys.add('widget:workout')
    if (hasTrack) keys.add('widget:music')
    if (hasShortcut) keys.add('widget:shortcut')
    if (permissions.content) keys.add('widget:history')
    if (permissions.fullAccess) keys.add('widget:notification')
    if (permissions.fullAccess) keys.add('widget:sleep')
    return keys
  }, [permissions, hasTrack, hasShortcut])
}
